import { Key, useEffect, useState } from 'react'
import { Link, useParams, useLocation } from 'react-router-dom'
import { User, GalleryItem } from '../interfaces/user'
import formatBelgianPhoneNumber from '../fonctions/formatBelgiumPhone'
import { Work, PracticeCategory } from '../interfaces/work'
import Modal from '../components/MyModal'
import { t } from 'i18next'
import { FaGlobe, FaPenFancy, FaPhone, FaSms, FaWhatsapp } from 'react-icons/fa'
import {
  IoIosWarning,
  IoMdArrowDropleftCircle,
  IoMdArrowDroprightCircle,
  IoMdMail,
  IoMdRefresh,
} from 'react-icons/io'
import { AiFillHome } from 'react-icons/ai'
import {
  MdCheckCircle,
  MdOutlineKeyboardDoubleArrowRight,
} from 'react-icons/md'
import formatDate from '../fonctions/formatDate'
import { FaLocationDot } from 'react-icons/fa6'
import Gallery from '../components/Gallery'
import { API_BASE_URL, X_API_KEY } from '../../services/api'
import { fetchWorkDetails } from '../../services/announcesWorks'
import { fetchUsers } from '../../services/fetchAllUser'
import { updateSearchFiltersAndRedirect } from '../fonctions/updateSearchFiltersAndRedirect'
import {
  hasPracticesWarnings,
  PracticesWarnings,
} from '../components/PracticesWarnings'
import SendMailModal from '../components/SendMailModal'
import ReportUserModal from '../components/ReportUserModal'

const dayMap: { [key: string]: number } = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 0,
}

function OneWork() {
  let { userId } = useParams()
  const { title, id } = useParams<{ title: string; id: string }>()
  const location = useLocation()
  const [work, setWork] = useState<Work | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [gallery, setGallery] = useState<GalleryItem[]>([])
  const [error, setError] = useState<string | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [photoIndex, setPhotoIndex] = useState(0)

  const [practiceData, setPracticeData] = useState<PracticeCategory[]>([])
  const [, setPracticeNames] = useState<string[]>([])
  const [isSendMailModalOpen, setIsSendMailModalOpen] = useState(false)
  const [isReportModalOpen, setIsReportModalOpen] = useState(false)
  const formatURL = (title: string, id: number) => {
    return `${title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')}/${id}`
  }
  const previousId = work ? formatURL(work.title, Number(work.id) - 1) : ''
  const nextId = work ? formatURL(work.title, Number(work.id) + 1) : ''
  useEffect(() => {
    const fetchData = async () => {
      if (!id) {
        setError('Invalid work ID')
        return
      }

      try {
        const data = await fetchWorkDetails(id)
        if (data.work) {
          setWork(data.work)
          setGallery(data.gallery || [])

          const users = await fetchUsers()
          const foundUser = users.find((u: User) => u.id === data.work.userId)
          setUser(foundUser ?? null)
        } else {
          setError('Work not found')
        }
      } catch (error) {
        setError('Failed to fetch work details')
        console.error('Error fetching work details:', error)
      }
    }

    fetchData()
  }, [id])

  useEffect(() => {
    const fetchWork = async () => {
      try {
        const data = await fetchWorkDetails(id!)
        setWork(data.work)
        setGallery(data.gallery)

        const users = await fetchUsers()
        const foundUser = users.find((u: User) => u.id === data.work.userId)
        setUser(foundUser ?? null)
      } catch (error) {
        setError('Failed to fetch Work')
        console.error('Error fetching work:', error)
      }
    }

    fetchWork()
  }, [id])

  const handleLinkClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    event.preventDefault()
    if (user?.address?.city) {
      updateSearchFiltersAndRedirect(user.address.city)
    }
  }

  useEffect(() => {
    const fetchPractices = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/practices`, {
          headers: {
            'x-api-key': X_API_KEY || '',
          },
        })
        if (!response.ok) {
          throw new Error('Network response was not ok')
        }
        const data = await response.json()
        setPracticeData(data.practiceList)

        if (work && work.practices) {
          const names = work.practices
            .map((id: number) => {
              for (const category of data.practiceList) {
                const practice = category.practices.find(
                  (p: { id: number }) => p.id === id,
                )
                if (practice) {
                  return practice.practiceName
                }
              }
              return null
            })
            .filter((name: string | null): name is string => name !== null)
          setPracticeNames(names)
        }
      } catch (error) {
        console.error('Error fetching practices:', error)
      }
    }

    fetchPractices()
  }, [work])

  const handlePrev = () => {
    setPhotoIndex((photoIndex + gallery.length - 1) % gallery.length)
  }

  const handleNext = () => {
    setPhotoIndex((photoIndex + 1) % gallery.length)
  }

  const isWithinTimeRange = (
    currentTime: string,
    start: string,
    end: string,
  ) => {
    const [currentHour, currentMin] = currentTime.split(':').map(Number)
    const [startHour, startMin] = start.split(':').map(Number)
    const [endHour, endMin] = end.split(':').map(Number)

    const current = currentHour * 60 + currentMin
    const startTime = startHour * 60 + startMin
    const endTime = endHour * 60 + endMin

    return current >= startTime && current <= endTime
  }
  const coverImage = gallery.find(item => item.cover === true)
  if (error) {
    return (
      <div>
        <div className='bg-redpink'>
          <div className='max-w-7xl pb-2 mx-auto font-light'>
            <div className='flex pt-2'>
              <Link
                to='/'
                className='text-white hover:text-anthracite transition-all'
              >
                <AiFillHome className='text-2xl' />
              </Link>
              <MdOutlineKeyboardDoubleArrowRight className='mx-2 text-white text-2xl' />
              <span className='text-white'>{t('error')}</span>
            </div>
          </div>
        </div>

        <div className='max-w-7xl mx-auto my-5'>
          <div className='flex items-center justify-center min-h-72 p-5 bg-grisclair rounded-lg'>
            <div className='text-center space-y-4'>
              <IoIosWarning className='text-redpink text-6xl mx-auto mb-4' />
              <h2 className='text-2xl font-semibold text-anthracite mb-2'>
                {t('ad-not-found')}
              </h2>
              <p className='text-gray-600 mb-6'>{t('work-not-found')}</p>
              <div className='space-x-4'>
                <Link
                  to='/'
                  className='bg-redpink text-white px-6 py-2 rounded hover:bg-anthracite transition-all inline-flex items-center'
                >
                  <AiFillHome className='mr-2' />
                  {t('dashboard.to-the-site')}
                </Link>
                <button
                  onClick={() => window.location.reload()}
                  className='bg-anthracite text-white px-6 py-2 rounded hover:bg-redpink transition-all inline-flex items-center'
                >
                  <IoMdRefresh className='mr-2' />
                  {t('reload-page')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  return (
    <div>
      <div className='bg-redpink'>
        <div className='max-w-7xl pb-2 mx-auto font-light flex justify-between '>
          <div className='flex pt-2'>
            <Link
              to='/'
              className='text-white hover:text-anthracite transition-all'
            >
              <AiFillHome className='text-2xl' />
            </Link>
            <MdOutlineKeyboardDoubleArrowRight className='mx-2 text-white text-2xl' />
            {Array.isArray(work?.status) &&
              work?.status.map((status: any, index: Key | null | undefined) => (
                <div key={index} className='flex'>
                  <Link
                    to={`/work?status=${encodeURIComponent(status)}`}
                    className='text-white hover:text-anthracite transition-all'
                  >
                    {t(`create-ad.${status}`)}
                  </Link>
                  <MdOutlineKeyboardDoubleArrowRight className='mx-2 text-white text-2xl' />
                </div>
              ))}
            <p className='text-white'>{work?.title}</p>
          </div>
          <div className='flex'>
            <Link to={`/work/${previousId}`}>
              <IoMdArrowDropleftCircle className='text-4xl hover:text-white transition-all' />
            </Link>
            <Link to={`/work/${nextId}`}>
              <IoMdArrowDroprightCircle className='text-4xl hover:text-white transition-all' />
            </Link>
          </div>
        </div>
      </div>
      <div className='bg-anthracite'>
        <div className='max-w-7xl mx-auto font-light flex justify-between text-white py-3'>
          <div className='flex'>
            <FaPenFancy className='text-redpink text-xl mr-2' />
            {user && (
              <>
                {t('posted-by')}{' '}
                <Link
                  to={`/search/user/${user.id}`}
                  className='text-redpink pl-2 hover:underline transition-all'
                >
                  {user.nickname}
                </Link>
                {work && (
                  <span className='pl-2'>({formatDate(work?.updatedAt)})</span>
                )}
                <button
                  className='text-redpink flex hover:underline ml-5'
                  onClick={() => setIsReportModalOpen(true)}
                >
                  <IoIosWarning className='text-2xl mr-2' /> {t('report-user')}
                </button>
              </>
            )}
          </div>
          <div className='flex'>
            {work?.emailOnly && (
              <Link
                to='#'
                className='flex hover:underline ml-3'
                onClick={e => {
                  e.preventDefault()
                  if (user?.email) {
                    setIsSendMailModalOpen(true)
                  }
                }}
              >
                <IoMdMail className='text-blue-500 mr-2 text-2xl' />
                {t('send-mail')}
              </Link>
            )}
            <>
              <a
                href={`tel:${user?.phone}`}
                className='flex hover:underline ml-4'
              >
                <FaPhone className='text-emerald-500 mr-2 text-xl' />
                {formatBelgianPhoneNumber(user?.phone ?? '')}
              </a>
            </>
          </div>
        </div>
      </div>
      <div className='max-w-7xl mx-auto flex py-7'>
        {error ? (
          <div className='flex items-center justify-center min-h-72 p-5 bg-grisclair rounded-lg'>
            <div className='text-center space-y-4'>
              <IoIosWarning className='text-redpink text-6xl mx-auto mb-4' />
              <h2 className='text-2xl font-semibold text-anthracite mb-2'>
                {t('ad-not-found')}
              </h2>
              <p className='text-gray-600 mb-6'>{t(error)}</p>
              <div className='space-x-4'>
                <Link
                  to='/'
                  className='bg-redpink text-white px-6 py-2 rounded hover:bg-anthracite transition-all inline-flex items-center'
                >
                  <AiFillHome className='mr-2' />
                  {t('dashboard.to-the-site')}
                </Link>
                <button
                  onClick={() => window.location.reload()}
                  className='bg-anthracite text-white px-6 py-2 rounded hover:bg-redpink transition-all inline-flex items-center'
                >
                  <IoMdRefresh className='mr-2' />
                  {t('reload-page')}
                </button>
              </div>
            </div>
          </div>
        ) : (
          work && (
            <>
              <div className='w-2/5'>
                <Gallery
                  gender={user?.userProfile?.gender}
                  gallery={gallery}
                  coverImage={coverImage}
                  statusToShow={0}
                />
                {gallery.length > 1 && (
                  <div className='mt-4 flex flex-wrap gap-2 justify-between'>
                    {gallery.map((image, index) => (
                      <img
                        key={index}
                        src={image.fileUrl}
                        alt={`Image ${index + 1}`}
                        className='w-20 h-20 object-cover cursor-pointer rounded hover:opacity-80'
                        onClick={() => {
                          setCurrentImageIndex(index)
                          setIsGalleryModalOpen(true)
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
              <div className='w-3/5 ml-7'>
                <p className='text-3xl'>{work.title}</p>
                <div className='flex'>
                  {Array.isArray(work?.status)
                    ? work?.status.map((status, index) => (
                        <span key={index} className='text-redpink'>
                          <Link
                            to={`/work?status=${encodeURIComponent(status)}`}
                            className='flex items-center justify-center bg-redpink text-white px-2 py-0 mr-2 mt-1 rounded hover:bg-anthracite transition-all'
                          >
                            {t(`create-ad.${status}`)}
                          </Link>
                        </span>
                      ))
                    : null}
                  <Link
                    className='flex bg-redpink text-white px-2 py-0 mr-2 mt-1 rounded hover:bg-anthracite transition-all'
                    to='/'
                    onClick={handleLinkClick}
                  >
                    <FaLocationDot className='mr-1 mt-1 text' />
                    {t(`${user?.address?.city}`)}
                  </Link>
                </div>
                {user?.address && (
                  <div className='bg-grisclair p-2 rounded-lg my-4 flex items-center'>
                    <FaLocationDot className='mr-2 text-redpink text-2xl' />
                    <span>
                      {user.address.street ? (
                        <>
                          {user.address.street} {user.address.number}
                          <br />
                        </>
                      ) : null}
                      {user.address.zip_code} {user.address.city}
                    </span>
                  </div>
                )}
                <p className='flex my-5 whitespace-pre-line'>
                  {work.description}
                </p>
                <div className='border border-1 border-anthracite overflow-hidden rounded-lg'>
                  <p className='text-lg bg-anthracite text-white rounded-t text-center py-1'>
                    {t('contact')}
                  </p>
                  <div className='flex'>
                    <div
                      className={`${
                        work.isAvailable ||
                        (Array.isArray(work.disponibility) &&
                          work.disponibility.length > 0)
                          ? 'w-1/2'
                          : 'w-full'
                      }`}
                    >
                      <div className='flex hover:bg-grisclair justify-between py-2  px-4'>
                        <a
                          href={`tel:${user?.phone}`}
                          className='flex justify-between w-full'
                        >
                          <span className='flex items-center'>
                            <FaPhone className='text-redpink mr-2 text-xl' />
                            <span>{t('phone')}</span>
                          </span>
                          <span className='text-redpink'>
                            {formatBelgianPhoneNumber(user?.phone ?? '')}
                          </span>
                        </a>
                      </div>
                      {work?.whatsapp && (
                        <div className='flex hover:bg-grisclair justify-between py-2  px-4'>
                          <a
                            href={`https://wa.me/${user?.phone}`}
                            className='flex justify-between w-full'
                          >
                            <span className='flex items-center'>
                              <FaWhatsapp className='text-whatsapp text-xl mr-2' />
                              <span>WhatsApp</span>
                            </span>
                            <span className='text-redpink'>
                              {formatBelgianPhoneNumber(user?.phone ?? '')}
                            </span>
                          </a>
                        </div>
                      )}
                      {work.website && (
                        <div className='flex hover:bg-grisclair justify-between py-2 px-4'>
                          <a
                            href={
                              work.website.startsWith('http')
                                ? work.website
                                : `http://${work.website}`
                            }
                            className='flex justify-between w-full'
                            target='_blank'
                            rel='noopener noreferrer'
                          >
                            <span className='flex items-center'>
                              <FaGlobe className='text-redpink text-xl mr-2' />
                              <span>{t('website')}</span>
                            </span>
                            <span className='text-redpink'>{work.website}</span>
                          </a>
                        </div>
                      )}

                      {work.callOnly && (
                        <p className='hover:bg-grisclair flex justify-center py-2 px-4'>
                          <span>
                            <FaPhone className='mr-2' />
                          </span>
                          {t('create-ad.call-only')}
                        </p>
                      )}
                      {work.smsOnly && (
                        <p className='hover:bg-grisclair flex justify-center py-2 px-4'>
                          <span>
                            <FaSms className='mr-2' />
                          </span>
                          {t('create-ad.sms-only')}
                        </p>
                      )}
                    </div>
                    {(work.isAvailable ||
                      (Array.isArray(work.disponibility) &&
                        work.disponibility.length > 0)) && (
                      <div className='w-1/2'>
                        {work.isAvailable ? (
                          <p className='hover:bg-grisclair flex justify-between py-2  px-4'>
                            <span className='text-center w-full'>
                              {t('create-ad.available247')}
                            </span>
                          </p>
                        ) : (
                          Array.isArray(work.disponibility) &&
                          work?.disponibility?.map(
                            (day: string, index: number) => {
                              const currentDay = new Date().getDay()
                              const isDayToday =
                                dayMap[day.toLowerCase()] === currentDay
                              const isOpen =
                                work.availabilityStart?.[index] !== '00:00' &&
                                work.availabilityEnd?.[index] !== '00:00'

                              return (
                                <p
                                  key={index}
                                  className='flex justify-between py-2  px-4 hover:bg-grisclair'
                                >
                                  <span
                                    className={`flex items-center ${
                                      isDayToday ? 'font-bold' : ''
                                    }`}
                                  >
                                    <span>
                                      {t(`create-ad.${day.toLowerCase()}`)}
                                    </span>
                                  </span>
                                  <span
                                    className={`text-redpink ${
                                      isOpen ? '' : 'text-gray-400'
                                    }`}
                                  >
                                    {work.availabilityStart &&
                                    work.availabilityEnd &&
                                    work.availabilityStart[index] === '00:00' &&
                                    work.availabilityEnd[index] === '00:00' ? (
                                      t('24h/24')
                                    ) : (
                                      <span className='flex'>
                                        {isDayToday && (
                                          <span className='text-green-500 mr-2 mt-0.5'>
                                            {isWithinTimeRange(
                                              new Date().toLocaleTimeString(
                                                'en-US',
                                                {
                                                  hour: '2-digit',
                                                  minute: '2-digit',
                                                  hour12: false,
                                                },
                                              ),
                                              work.availabilityStart?.[index] ||
                                                '',
                                              work.availabilityEnd?.[index] ||
                                                '',
                                            ) && <MdCheckCircle />}
                                          </span>
                                        )}
                                        {work.availabilityStart?.[index] ?? ''}
                                        <span className='px-1'>{t('to')}</span>
                                        {work.availabilityEnd?.[index] ?? ''}
                                      </span>
                                    )}
                                  </span>
                                </p>
                              )
                            },
                          )
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {work.practices && work.practices.length > 0 && (
                  <div className='border mt-5 border-1 border-anthracite rounded-lg pb-2'>
                    <p className='text-lg bg-anthracite text-white rounded-t text-center py-1'>
                      {t('services')}
                    </p>
                    {practiceData.map(category => {
                      const filteredPractices = category.practices.filter(
                        practice => work.practices.includes(practice.id),
                      )
                      return filteredPractices.length > 0 ? (
                        <div className='p-2 pb-0' key={category.id}>
                          <div className='w-full text-center bg-grisclair2 rounded-lg mb-2 uppercase p-1'>
                            {t(`create-ad.${category.categoryName}`)}
                          </div>
                          <div className='w-full grid grid-cols-3 text-center'>
                            {filteredPractices.map(practice => (
                              <div
                                className='relative px-2 py-3 text-sm font-semibold hover:bg-grisclair rounded-lg'
                                key={practice.id}
                              >
                                {t(`practices.${practice.practiceName}`)}
                                {hasPracticesWarnings(
                                  practice.practiceName,
                                ) && (
                                  <PracticesWarnings
                                    practiceName={practice.practiceName}
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null
                    })}
                  </div>
                )}
              </div>
            </>
          )
        )}
      </div>
      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onPrev={handlePrev}
        onNext={handleNext}
      >
        {gallery.length > 0 && (
          <img
            src={gallery[photoIndex]?.fileUrl}
            alt={gallery[photoIndex]?.fileName}
            className='max-w-full max-h-[90vh] rounded-lg'
          />
        )}
      </Modal>
      {user?.email && (
        <SendMailModal
          isOpen={isSendMailModalOpen}
          onClose={() => setIsSendMailModalOpen(false)}
          recipientEmail={user.email}
          recipientName={user.nickname ?? ''}
        />
      )}
      {user && (
        <ReportUserModal
          isOpen={isReportModalOpen}
          onClose={() => setIsReportModalOpen(false)}
          userNickname={user.nickname}
          userId={user.id}
        />
      )}
      {isGalleryModalOpen && (
        <div className='fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center'>
          <button
            className='absolute top-5 right-5 text-white text-3xl'
            onClick={() => setIsGalleryModalOpen(false)}
          >
            &times;
          </button>

          <button
            onClick={() =>
              setCurrentImageIndex(prev =>
                prev === 0 ? gallery.length - 1 : prev - 1,
              )
            }
            className='absolute left-4 text-white text-4xl'
          >
            &#10094;
          </button>

          <img
            src={gallery[currentImageIndex].fileUrl}
            alt={`Image ${currentImageIndex + 1}`}
            className='max-w-[90%] max-h-[90%] rounded-lg shadow-lg'
          />

          <button
            onClick={() =>
              setCurrentImageIndex(prev =>
                prev === gallery.length - 1 ? 0 : prev + 1,
              )
            }
            className='absolute right-4 text-white text-4xl'
          >
            &#10095;
          </button>
        </div>
      )}
    </div>
  )
}

export default OneWork
